/**
 * Unified Block Rendering System
 *
 * Public exports for the consolidated block rendering architecture.
 *
 * ## Usage
 *
 * ```tsx
 * import { BlockRenderProvider, BlockRenderer } from '@/components/blocks'
 *
 * function MyEditor() {
 *   return (
 *     <BlockRenderProvider value={{
 *       mode: 'editor',
 *       zoom: 100,
 *       coordinateTransform: myTransform,
 *       onSelect: handleSelect
 *     }}>
 *       {blocks.map(block => (
 *         <BlockRenderer key={block.id} block={block} />
 *       ))}
 *     </BlockRenderProvider>
 *   )
 * }
 * ```
 */

// Main renderer
export { BlockRenderer } from './BlockRenderer'
export { BlockErrorBoundary } from './BlockErrorBoundary'

// Context
export { BlockRenderProvider, useBlockRenderContext } from './BlockRenderContext'

// Registry
export { BlockVisualRegistry, getVisualForBlockType } from './Registry'

// Types
export type {
  RenderMode,
  Point,
  PixelPoint,
  Dimensions,
  CoordinateTransform,
  AnimationState,
  BlockRenderContextValue,
  BlockVisualProps,
  BlockBehaviorProps,
} from './Types'

// Behavior wrappers
export { Draggable, Selectable } from './behaviors/editor'
export { AnimatedPosition } from './behaviors/animated'

// Visual components
export { PlayerVisual } from './visuals/canvas'
